/**
 * Shared web → native bridge for the in-app (Expo WebView) client.
 *
 * When the web app is mounted inside the Expo shell, the native host injects
 * `window.isExpoApp` and `window.ReactNativeWebView`. Features talk to the host
 * by posting a JSON `{ type, data? }` message; the push and Plaid features
 * already use this exact convention (see `push/expoPushImplementation.ts` and
 * `plaid/hostedLink.ts`). This module centralizes the capability gate and the
 * outbound sender so new features (e.g. navigation events) don't have to
 * re-declare the globals or re-implement the guard.
 */

declare global {
  interface Window {
    isExpoApp?: boolean
    // Set by native builds that own sign-in and can answer `request-auth-session`
    // (see `expo-owned-signin` plan). Distinct from `isExpoApp` — which is true in
    // every shipped native build — so the web's native-auth handoff only engages
    // for builds that actually support it, and older native builds fall through to
    // the in-webview sign-in flow instead of hanging on a session that never arrives.
    expoSupportsAuth?: boolean
    /**
     * Set by AppWebView when this document is a single page under the native
     * app bar (submission, help, advance, checklist), not the full-app webview.
     * Also latched from `?embedded=1` so the desktop iframe stand-in — which
     * cannot inject this flag — hides the same chrome.
     */
    isEmbeddedApp?: boolean
    ReactNativeWebView?: { postMessage: (message: string) => void }
  }
}

export function isExpoApp(): boolean {
  return !!(window.isExpoApp && window.ReactNativeWebView)
}

/**
 * True when this page is showing inside the native shell's app bar and should
 * not draw its own header, back arrow, or tab bar.
 *
 * The full-app webview (no `native_app_shell`) does not set the flag: that
 * document is the whole UI and still needs its chrome. A normal browser visit
 * never sets it either.
 */
export function isEmbeddedApp(): boolean {
  if (typeof window === 'undefined') return false
  if (window.isEmbeddedApp) return true
  // Latch the query so a client-side navigation, which drops `?embedded=1`,
  // stays embedded for the rest of this document.
  if (new URLSearchParams(window.location.search).get('embedded') === '1') {
    window.isEmbeddedApp = true
    return true
  }
  return false
}

/**
 * True when the native host owns sign-in and will answer `request-auth-session`.
 * Gate the native-auth handoff on this function (not directly on `isExpoApp()` —
 * this function already includes that check), so already-installed native builds
 * that predate the handoff are unaffected.
 */
export function expoSupportsAuth(): boolean {
  return !!(isExpoApp() && window.expoSupportsAuth)
}

/**
 * Outbound message types the web app sends to the native Expo host via
 * `postToNative`. Keep this the single source of truth for the bridge
 * vocabulary — adding a new event here makes it discoverable via autocomplete
 * and turns a typo at the call site into a compile error.
 *
 * Auth handoff vocabulary (see the `expo-owned-signin` plan):
 * - `request-auth-session`  — ask native for the current Supabase session
 * - `auth-session-updated`  — the webview minted a new token pair (e.g. after MFA
 *                             step-up); native should adopt it and keep owning refresh
 * - `sign-out`              — the webview signed the user out; native should too
 */
export type NativeMessageType =
  | 'navigation-change'
  | 'open-plaid-hosted-link'
  | 'request-auth-session'
  | 'auth-session-updated'
  | 'sign-out'

/**
 * Name of the DOM CustomEvent the native host dispatches into the webview to
 * deliver a Supabase session (`detail: { access_token, refresh_token } | null`),
 * mirroring the existing `push-token` / `plaid-hosted-link-result` inbound events.
 * Kept here as the single source of truth so the native side and the web listener
 * can't drift.
 */
export const AUTH_SESSION_EVENT = 'auth-session'

/** Payload native sends on `AUTH_SESSION_EVENT` when it holds a session. */
export interface AuthSessionEventDetail {
  access_token: string
  refresh_token: string
}

/**
 * The `AUTH_SESSION_EVENT` CustomEvent shape. `detail` is `null` when native has
 * no session (e.g. it signed the user out) — see the `expo-owned-signin` plan's
 * `auth-session: null` sign-out flow.
 */
export type AuthSessionEvent = CustomEvent<AuthSessionEventDetail | null>

/**
 * The auth handoff message types — see the vocabulary note on `NativeMessageType`.
 * `postToNative` refuses to send these to a native build that hasn't advertised
 * `expoSupportsAuth()`, so a caller that forgets the guard fails safe (silent
 * no-op) instead of sending an old native build a message it will never answer,
 * which would otherwise leave the web side hanging.
 */
const AUTH_MESSAGE_TYPES: ReadonlySet<NativeMessageType> = new Set([
  'request-auth-session',
  'auth-session-updated',
  'sign-out',
])

/**
 * Send a `{ type, data }` message to the native Expo host. No-op when the app
 * isn't running inside the Expo WebView, so callers can invoke it
 * unconditionally. Also no-ops for the auth message types when the native host
 * hasn't advertised `expoSupportsAuth()` — see `AUTH_MESSAGE_TYPES`.
 */
export function postToNative(type: NativeMessageType, data?: unknown): void {
  if (!isExpoApp()) return
  if (AUTH_MESSAGE_TYPES.has(type) && !expoSupportsAuth()) return
  window.ReactNativeWebView?.postMessage(JSON.stringify({ type, data }))
}
